/**
 * Phase 7 Gelombang 1 — Scenario Comparison + Plan vs Actual
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Scale, LineChart as LineIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const fmtRp = (x) => x === null || x === undefined ? "—" :
  `Rp ${new Intl.NumberFormat("id-ID").format(Math.round(x))}`;
const COLORS = ["#1e3a5f", "#059669", "#f59e0b", "#e11d48", "#8b5cf6"];

export default function ExecutiveScenarioComparison() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [pvaScenario, setPvaScenario] = useState("");
  const [pvaRows, setPvaRows] = useState([]);

  const load = () => {
    setErr(null);
    api.get(`/executive/scenario-comparison?year=${year}&month=${month}`)
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengakses perbandingan skenario.");
        else setErr(formatApiError(e));
      });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const loadPva = (sid) => {
    setPvaScenario(sid);
    setPvaRows([]);
    if (!sid) return;
    api.get(`/executive/plan-vs-actual?scenario_id=${sid}`)
      .then(({ data }) => setPvaRows(data.rows || []))
      .catch((e) => setErr(formatApiError(e)));
  };

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-comparison-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-comparison-no-access">
          {err}
        </div>
      </div>
    );
  }

  // Build comparison chart data: array of {period, [scenarioA_revenue]: ...}
  const periodSet = new Set();
  items.forEach((it) => (it.result?.months || []).forEach((m) => periodSet.add(m.period)));
  const periods = Array.from(periodSet).sort();
  const chartData = periods.map((p) => {
    const row = { period: p };
    items.forEach((it) => {
      const m = (it.result?.months || []).find((x) => x.period === p);
      row[`${it.scenario.name} (${it.scenario.scenario_type})`] = m ? m.kpis.net_profit : null;
    });
    return row;
  });

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exec-comparison-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Scale className="h-6 w-6 text-sky-700" /> Perbandingan Skenario & Plan vs Actual
        </h1>
        <p className="text-xs md:text-sm text-slate-500">Bandingkan Base / Optimistic / Conservative untuk periode terpilih dan tinjau Plan vs Actual bulanan.</p>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Filter Periode</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div>
              <Label className="text-xs">Tahun</Label>
              <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
                       className="w-28" data-testid="cmp-year" />
            </div>
            <div>
              <Label className="text-xs">Bulan</Label>
              <Input type="number" min={1} max={12} value={month}
                       onChange={(e) => setMonth(Number(e.target.value))}
                       className="w-20" data-testid="cmp-month" />
            </div>
            <Button variant="outline" onClick={load} data-testid="cmp-refresh">Muat Ulang</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Ringkasan 12-bulan per Skenario</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Skenario</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Omzet</TableHead>
                <TableHead className="text-right">Gross Profit</TableHead>
                <TableHead className="text-right">Net Profit</TableHead>
                <TableHead className="text-right">Ending Cash</TableHead>
                <TableHead className="text-right">WC akhir</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-xs text-slate-400 italic py-6">
                  Belum ada skenario untuk periode ini
                </TableCell></TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.scenario.id} data-testid={`cmp-row-${it.scenario.id}`}>
                  <TableCell className="text-xs font-semibold">{it.scenario.name}</TableCell>
                  <TableCell className="text-xs">{it.scenario.scenario_type}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{it.scenario.status}</Badge></TableCell>
                  <TableCell className="text-right text-xs">{fmtRp(it.result?.totals?.revenue)}</TableCell>
                  <TableCell className="text-right text-xs">{fmtRp(it.result?.totals?.gross_profit)}</TableCell>
                  <TableCell className="text-right text-xs">{fmtRp(it.result?.totals?.net_profit)}</TableCell>
                  <TableCell className="text-right text-xs">{fmtRp(it.result?.totals?.ending_cash)}</TableCell>
                  <TableCell className="text-right text-xs">{fmtRp(it.result?.totals?.working_capital_last)}</TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => loadPva(it.scenario.id)}
                             data-testid={`cmp-pva-${it.scenario.id}`}>
                      <LineIcon size={11} className="mr-1" />Plan vs Actual
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {chartData.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Net Profit — Perbandingan Monthly</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 300, minWidth: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="period" fontSize={11} />
                  <YAxis fontSize={10} />
                  <Tooltip formatter={(v) => fmtRp(v)} />
                  <Legend />
                  {items.slice(0, 5).map((it, i) => (
                    <Line key={it.scenario.id} type="monotone"
                            dataKey={`${it.scenario.name} (${it.scenario.scenario_type})`}
                            stroke={COLORS[i]} strokeWidth={2} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan vs Actual */}
      {pvaScenario && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Plan vs Actual</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Periode</TableHead>
                  <TableHead className="text-right">Plan Omzet</TableHead>
                  <TableHead className="text-right">Actual Omzet</TableHead>
                  <TableHead className="text-right">Var %</TableHead>
                  <TableHead className="text-right">Plan NP</TableHead>
                  <TableHead className="text-right">Actual NP</TableHead>
                  <TableHead className="text-right">Var %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pvaRows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-xs text-slate-400 italic py-4">
                    Belum ada data untuk ditampilkan
                  </TableCell></TableRow>
                )}
                {pvaRows.map((r) => (
                  <TableRow key={r.period} data-testid={`pva-${r.period}`}>
                    <TableCell className="font-mono text-xs">{r.period}</TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(r.plan?.revenue)}</TableCell>
                    <TableCell className="text-right text-xs">
                      {r.actual_available ? fmtRp(r.actual?.revenue) :
                        <span className="text-slate-400 italic">Belum tersedia</span>}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {r.variance?.revenue?.percent !== null && r.variance?.revenue?.percent !== undefined ? (
                        <span className={r.variance.revenue.favorable ? "text-emerald-700" : "text-rose-700"}>
                          {r.variance.revenue.percent.toFixed(1)}%
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right text-xs">{fmtRp(r.plan?.net_profit)}</TableCell>
                    <TableCell className="text-right text-xs">
                      {r.actual_available ? fmtRp(r.actual?.net_profit) :
                        <span className="text-slate-400 italic">Belum tersedia</span>}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {r.variance?.net_profit?.percent !== null && r.variance?.net_profit?.percent !== undefined ? (
                        <span className={r.variance.net_profit.favorable ? "text-emerald-700" : "text-rose-700"}>
                          {r.variance.net_profit.percent.toFixed(1)}%
                        </span>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
